/**
 * Regras da tela de Pagamentos (AF-4139): filtros, seleção, intervalo de datas e agrupamento das
 * faturas por pagamento. Puras e fora do serviço HTTP: os testes da tela as rodam de verdade.
 *
 * Os filtros rodam no front porque o dia (ou o intervalo) vem inteiro numa página só, e os
 * totalizadores somam exatamente as linhas que a tabela mostra.
 */
import type { PaymentRow, PaymentStatus } from './billingApi';

export type AmountOperator = '' | 'EQ' | 'LT' | 'GT' | 'BETWEEN';

/** Pagamento em conta Escrow (AF-4155): "-" não filtra. */
export type EscrowFilter = '' | 'NO' | 'YES';

export interface PaymentFilters {
    statuses: PaymentStatus[];
    amountOperator: AmountOperator;
    amount: number | null;
    amountTo: number | null;
    paymentType: string;
    paymentMethod: string;
    assetType: string;
    payerName: string;
    payerDocument: string;
    accountAlias: string;
    escrow: EscrowFilter;
}

export const MAX_RANGE_DAYS = 92;

const ALL_STATUSES: PaymentStatus[] = ['PENDING', 'MARKED', 'IDENTIFIED'];

export const DEFAULT_PAYMENT_FILTERS: PaymentFilters = {
    statuses: ALL_STATUSES,
    amountOperator: '',
    amount: null,
    amountTo: null,
    paymentType: '',
    paymentMethod: '',
    assetType: '',
    payerName: '',
    payerDocument: '',
    accountAlias: '',
    escrow: '',
};

/** O PIX e o boleto casam consigo; "Transferência (TED)" casa também o TED gravado antes dela. */
const METHOD_ALIASES: Record<string, string[]> = { TRANSFER: ['TRANSFER', 'TED'] };

const plain = (text: string): string =>
    text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const digits = (text: string | null | undefined): string => (text || '').replace(/\D/g, '');

const cents = (value: number): number => Math.round(value * 100);

const matchesAmount = (amount: number | null, filters: PaymentFilters): boolean => {
    const { amountOperator: operator, amount: reference, amountTo } = filters;
    if (!operator || reference == null) return true;
    const value = cents(amount || 0);
    const low = cents(reference);
    if (operator === 'EQ') return value === low;
    if (operator === 'LT') return value < low;
    if (operator === 'GT') return value > low;
    const high = amountTo == null ? Infinity : cents(amountTo);
    return value >= Math.min(low, high) && value <= Math.max(low, high);
};

export const activeFilterCount = (filters: PaymentFilters): number => [
    filters.statuses.length !== ALL_STATUSES.length,
    !!filters.amountOperator && filters.amount != null,
    !!filters.paymentType,
    !!filters.paymentMethod,
    !!filters.assetType,
    !!plain(filters.payerName),
    !!digits(filters.payerDocument),
    !!filters.accountAlias,
    !!filters.escrow,
].filter(Boolean).length;

export const matchesPaymentFilters = (row: PaymentRow, filters: PaymentFilters): boolean => {
    const method = row.paymentMethod || '';
    const name = plain(filters.payerName);
    const document = digits(filters.payerDocument);
    return filters.statuses.includes(row.status)
        && matchesAmount(row.amount, filters)
        && (!filters.paymentType || row.paymentType === filters.paymentType)
        && (!filters.paymentMethod
            || (METHOD_ALIASES[filters.paymentMethod] || [filters.paymentMethod]).includes(method))
        && (!filters.assetType || method === filters.assetType)
        && (!name || plain(row.payer?.name || '').includes(name))
        && (!document || digits(row.payer?.documentNumber).includes(document))
        && (!filters.accountAlias || row.receivingBankAccount?.aliasName === filters.accountAlias)
        && (!filters.escrow || !!row.escrowAccount === (filters.escrow === 'YES'));
};

export const filterPayments = (rows: PaymentRow[], filters: PaymentFilters): PaymentRow[] =>
    rows.filter(row => matchesPaymentFilters(row, filters));

export const selectedInvoicesCount = (rows: PaymentRow[], selectedIds: Set<string>): number =>
    rows.filter(row => selectedIds.has(row.paymentId))
        .reduce((sum, row) => sum + row.linkedInvoicesCount, 0);

/** O que os gráficos somam: a seleção, quando ela é uma parte; senão, tudo o que a tabela mostra. */
export const panelRows = (visibleRows: PaymentRow[], selectedIds: Set<string>): PaymentRow[] => {
    const chosen = visibleRows.filter(row => selectedIds.has(row.paymentId));
    return chosen.length > 0 && chosen.length < visibleRows.length ? chosen : visibleRows;
};

const utcDay = (day: string): number => {
    const [year, month, date] = day.split('-').map(Number);
    return Date.UTC(year, month - 1, date);
};

export const rangeError = (from: string, to: string): string | null => {
    const days = Math.round((utcDay(to) - utcDay(from)) / 86_400_000) + 1;
    return days > MAX_RANGE_DAYS ? `O intervalo pode ter no máximo ${MAX_RANGE_DAYS} dias.` : null;
};

/** O pagamento da linha: o que a sinaliza, ou o que a efetivou. */
export const paymentOfInvoice = (invoice: any): string | null =>
    invoice.paymentSignalledBy ?? invoice.details?.paidBy ?? null;

export interface PaymentGroup {
    payment: PaymentRow;
    invoices: any[];
}

/** As faturas de cada pagamento, na ordem dos pagamentos; pagamento sem fatura não entra. */
export const groupByPayment = (invoices: any[], payments: PaymentRow[]): PaymentGroup[] => payments
    .map(payment => ({
        payment,
        invoices: invoices.filter(invoice => paymentOfInvoice(invoice) === payment.paymentId),
    }))
    .filter(group => group.invoices.length > 0);
